import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { IFireQuoteCardDetails } from 'Components/Fire/FireQuotComp'
import {
  IBgrQuotePlan,
  IBgrQuoteResponse,
  IBgrVerifyCkycResponse,
} from 'Models/BOB/ResponseModels/Bgr/bgrResponse'
import { IOccupancy } from 'Pages/Fire/PropertyDetails'
import { INomineeFormInput } from 'Pages/Sachet/BGR/CommonComponent/NomineeDetails'
import { ILifePincode } from 'Services/API/BOB/bob_master.api'
import { ISpData } from 'Models/ResponseModels/Equitas'

interface IPropertyDetails {
  [key: string]: string | number | boolean | string[]
}
export interface IDynamicObject {
  [key: string]: string | number | boolean | any
}

export interface IFireState {
  propertyDetails: IPropertyDetails
  pageOneAnswers: IPropertyDetails
  pageTwoAnswers: IPropertyDetails
  pageThreeAnswers: IPropertyDetails
  selectedQuote?: IFireQuoteCardDetails
  riskPincode?: ILifePincode
  riskOccupancy?: IOccupancy
  QuotationFilters: any
  quote_res: {
    trace_id: string
  } | null
  filters?: IDynamicObject
  addOn?: IDynamicObject
  quote_no?: string
  additionalCoverages?: IDynamicObject
  ckycData: {
    ckycCustomerData: {
      documentType: { label: string; value: string } | null
      documentNumber: string
      name?: string
      mobile?: string
      gender?: string
      ohterDocumentType: { label: string; value: string } | null
      isKycConsent: boolean
      dob?: string
    }
    ckycResponse: IBgrVerifyCkycResponse | null
    ckycResponseOvd?: IBgrVerifyCkycResponse | null
  }

  selectedPlan?: IFireQuoteCardDetails | null
  isBgrQuotationApiLoading: boolean
  risk_address: IDynamicObject
  insured_details: IDynamicObject
  nominee_details: INomineeFormInput[]
  bgr_consent_id: string
  bgr_consent_status: string
  bgr_initial_consent_status: string
  plan_quote_res: { premium: number; quote_no: string } | null
  quoteListArray: IBgrQuotePlan[]
  paymentResponse: any
  riskAddress: any
  safety_details?: any
  fire_sp_consent_id: string
  fire_sp_consent_status: string
  fire_sp_consent_sent_at: number
  selectedSp?: ISpData
}

const initialState: IFireState = {
  propertyDetails: {},
  pageOneAnswers: {},
  pageTwoAnswers: {},
  riskAddress: {},
  pageThreeAnswers: {},
  riskPincode: {},
  quote_res: null,
  filters: {},
  ckycData: {
    ckycCustomerData: {
      documentNumber: '',
      ohterDocumentType: null,
      documentType: null,
      dob: '',
      isKycConsent: false,
    },
    ckycResponse: null,
  },
  addOn: {},
  additionalCoverages: {},
  selectedPlan: null,
  isBgrQuotationApiLoading: false,
  risk_address: {},
  insured_details: {},
  nominee_details: [],
  bgr_consent_id: '',
  bgr_consent_status: 'intial',
  bgr_initial_consent_status: 'intial',
  plan_quote_res: null,
  quoteListArray: [],
  paymentResponse: '',
  QuotationFilters: {},
  safety_details: null,
  fire_sp_consent_id: '',
  fire_sp_consent_status: 'initial',
  fire_sp_consent_sent_at: 0,
  selectedSp: undefined,
}

const fireSlice = createSlice({
  name: 'fireHome',
  initialState: initialState,
  reducers: {
    resetFire: () => initialState,
    updatePropertyDetails: (state, action: PayloadAction<IPropertyDetails>) => {
      state.propertyDetails = action.payload
    },
    updateQuoteNumber: (state, action: PayloadAction<string>) => {
      state.quote_no = action.payload
    },
    updateRiskAddress: (state, action: PayloadAction<any>) => {
      state.riskAddress = action.payload
    },
    updatePageOneAnswers: (state, action: PayloadAction<IPropertyDetails>) => {
      state.pageOneAnswers = action.payload
    },
    updateSelectedQuote: (state, action: PayloadAction<IFireQuoteCardDetails>) => {
      state.selectedQuote = action.payload
    },
    updatePageTwoAnswers: (state, action: PayloadAction<IPropertyDetails>) => {
      state.pageTwoAnswers = action.payload
    },
    updatePageThreeAnswers: (state, action: PayloadAction<IPropertyDetails>) => {
      state.pageThreeAnswers = action.payload
    },
    updateRiskPincode: (state, action: PayloadAction<ILifePincode>) => {
      state.riskPincode = action.payload
    },
    updateRiskOccupancy: (state, action: PayloadAction<IOccupancy>) => {
      state.riskOccupancy = action.payload
    },
    updateQuotationFilters: (state, action) => {
      state.QuotationFilters = action.payload
    },
    updateBgrRiskPincode: (state, action: PayloadAction<ILifePincode>) => {
      state.riskPincode = action.payload
    },
    updateQuoteListArray: (state, action: PayloadAction<IBgrQuotePlan[]>) => {
      state.quoteListArray = action.payload
    },
    updateFirequoteRes: (
      state,
      action: PayloadAction<{
        trace_id: string
      } | null>,
    ) => {
      state.quote_res = action.payload
    },
    updateSafetyDetails: (state, action: PayloadAction<any>) => {
      state.safety_details = action.payload
    },

    updateBgrCkycCustomerData: (
      state,
      action: PayloadAction<{
        documentType: { label: string; value: string } | null
        otherDocumentType: { label: string; value: string } | null
        documentNumber: string
        dob?: string
        name?: string
        mobile?: string
        gender?: string
        isKycConsent: boolean
      }>,
    ) => {
      const {
        documentNumber,
        documentType,
        dob,
        name,
        mobile,
        gender,
        isKycConsent,
        otherDocumentType,
      } = action.payload

      if (documentNumber) {
        state.ckycData.ckycCustomerData.documentNumber = documentNumber
      }
      if (documentType) {
        state.ckycData.ckycCustomerData.documentType = documentType
      }
      if (isKycConsent !== undefined) {
        state.ckycData.ckycCustomerData.isKycConsent = isKycConsent
      }
      if (dob) {
        state.ckycData.ckycCustomerData.dob = dob
      }
      if (name) {
        state.ckycData.ckycCustomerData.name = name
      }
      if (mobile) {
        state.ckycData.ckycCustomerData.mobile = mobile
      }
      if (gender) {
        state.ckycData.ckycCustomerData.gender = gender
      }
      if (otherDocumentType) {
        state.ckycData.ckycCustomerData.ohterDocumentType = otherDocumentType
      }

      // state.ckycData.ckycCustomerData.dob = dob ?? ''
    },
    updateBgrCkycResponse: (state, action: PayloadAction<IBgrVerifyCkycResponse>) => {
      state.ckycData.ckycResponse = action.payload
    },
    updateBgrCkycResponseOvd: (state, action: PayloadAction<IBgrVerifyCkycResponse>) => {
      state.ckycData.ckycResponseOvd = action.payload
    },
    updateBgrFilters: (state, action: PayloadAction<IDynamicObject>) => {
      const { ownership, building_type, no_of_floor, construction_type, katcha_construction } =
        action.payload
      if (ownership) {
        state.propertyDetails.ownership = ownership
      }
      if (String(building_type).toLowerCase() === 'multistory') {
        state.propertyDetails.building_type = building_type
        state.propertyDetails.no_of_floor = no_of_floor
      } else if (String(building_type).toLowerCase() === 'standalone') {
        state.propertyDetails.building_type = building_type
        delete state.propertyDetails.no_of_floor
      }
      if (String(construction_type).toLowerCase() === 'pucca') {
        state.propertyDetails.construction_type = construction_type
        delete state.propertyDetails.katcha_construction
      } else if (String(construction_type).toLowerCase() === 'katcha') {
        state.propertyDetails.construction_type = construction_type
        state.propertyDetails.katcha_construction = katcha_construction
      }
      // update filters
      state.filters = action.payload
    },
    resetBgrCkyc: (state) => {
      state.ckycData = initialState.ckycData
    },
    // clears only the verify result (what locks the document picker), keeping what the
    // customer entered on CKYC
    resetFireCkycResponse: (state) => {
      state.ckycData.ckycResponse = null
      state.ckycData.ckycResponseOvd = null
    },
    updateBgrAddOn: (state, action: PayloadAction<IDynamicObject>) => {
      state.addOn = action.payload
    },
    updateBgrAdditionalCoverages: (state, action: PayloadAction<IDynamicObject>) => {
      state.additionalCoverages = action.payload
    },
    updateSelectedPlan: (state, action: PayloadAction<IFireQuoteCardDetails>) => {
      state.selectedPlan = action.payload
    },
    updateIsBgrQuotationApiLoading: (state, action: PayloadAction<boolean>) => {
      state.isBgrQuotationApiLoading = action.payload
    },
    updateRisk_address: (state, action: PayloadAction<IDynamicObject>) => {
      state.risk_address = action.payload
    },
    updateInsured_details: (state, action: PayloadAction<IDynamicObject>) => {
      state.insured_details = action.payload
    },
    updateNominee_details: (state, action: PayloadAction<INomineeFormInput[]>) => {
      state.nominee_details = action.payload
    },
    updateBgrConsentId: (state, action: PayloadAction<string>) => {
      state.bgr_consent_id = action.payload
    },
    updateBgrConsentStatus: (state, action: PayloadAction<'intial' | 'pending' | 'success'>) => {
      state.bgr_consent_status = action.payload
    },
    updateBgrInitialConsentStatus: (
      state,
      action: PayloadAction<'intial' | 'pending' | 'success'>,
    ) => {
      state.bgr_initial_consent_status = action.payload
    },
    updateFireSpConsentId: (state, action: PayloadAction<string>) => {
      state.fire_sp_consent_id = action.payload
    },
    updateFireSpConsentStatus: (
      state,
      action: PayloadAction<'initial' | 'pending' | 'success' | 'not-sent' | 'rejected'>,
    ) => {
      state.fire_sp_consent_status = action.payload
    },
    updateFireSpConsentSentAt: (state, action: PayloadAction<number>) => {
      state.fire_sp_consent_sent_at = action.payload
    },
    updateFireSelectedSp: (state, action: PayloadAction<ISpData | undefined>) => {
      state.selectedSp = action.payload
    },
    updateBgrPlanQuoteRes: (
      state,
      action: PayloadAction<{ premium: number; quote_no: string } | null>,
    ) => {
      state.plan_quote_res = action.payload
    },
    updateFirePaymentResponse: (state, action: PayloadAction<any>) => {
      state.paymentResponse = action.payload
    },
    resetBgrStore: () => initialState,
    /**
     * Restores the complete fire journey state while resuming a dropped off lead.
     * Only the keys present in the saved lead data are restored, rest keep their current value.
     */
    updateFireAllData: (state, action: PayloadAction<Partial<IFireState>>) => {
      const savedFireData = action.payload
      if (!savedFireData) return state
      const restoredData = Object.fromEntries(
        Object.entries(savedFireData).filter(([, value]) => value !== undefined),
      ) as Partial<IFireState>
      return { ...state, ...restoredData }
    },
    updateBgrAllData: (state, action: PayloadAction<IFireState>) => {
      const {
        propertyDetails,
        riskPincode,
        quote_res,
        filters,
        addOn,
        ckycData,
        additionalCoverages,
        selectedPlan,
        isBgrQuotationApiLoading,
        risk_address,
        insured_details,
        nominee_details,
        bgr_consent_id,
        bgr_consent_status,
        plan_quote_res,
        quoteListArray,
        paymentResponse,
      } = action.payload
      if (propertyDetails !== undefined) {
        state.propertyDetails = propertyDetails
      }
      if (ckycData !== undefined) {
        state.ckycData = ckycData
      }
      if (riskPincode !== undefined) {
        state.riskPincode = riskPincode
      }
      if (quote_res !== undefined) {
        state.quote_res = quote_res
      }
      if (filters !== undefined) {
        state.filters = filters
      }
      if (addOn !== undefined) {
        state.addOn = addOn
      }
      if (additionalCoverages !== undefined) {
        state.additionalCoverages = additionalCoverages
      }
      if (selectedPlan !== undefined) {
        state.selectedPlan = selectedPlan
      }
      if (isBgrQuotationApiLoading !== undefined) {
        state.isBgrQuotationApiLoading = isBgrQuotationApiLoading
      }
      if (risk_address !== undefined) {
        state.risk_address = risk_address
      }
      if (insured_details !== undefined) {
        state.insured_details = insured_details
      }
      if (nominee_details !== undefined) {
        state.nominee_details = nominee_details
      }
      if (bgr_consent_id !== undefined) {
        state.bgr_consent_id = bgr_consent_id
      }
      if (bgr_consent_status !== undefined) {
        state.bgr_consent_status = bgr_consent_status
      }
      if (plan_quote_res !== undefined) {
        state.plan_quote_res = plan_quote_res
      }
      if (quoteListArray !== undefined) {
        state.quoteListArray = quoteListArray
      }
      if (paymentResponse !== undefined) {
        state.paymentResponse = paymentResponse
      }
    },
  },
})

const { reducer } = fireSlice

const {
  resetFire,
  updatePageOneAnswers,
  updatePageTwoAnswers,
  updatePageThreeAnswers,
  updatePropertyDetails,
  updateBgrRiskPincode,
  updateFirequoteRes,
  updateBgrFilters,
  updateBgrAddOn,
  updateBgrAdditionalCoverages,
  updateSelectedPlan,
  updateSelectedQuote,
  updateRiskPincode,
  updateIsBgrQuotationApiLoading,
  resetBgrCkyc,
  resetFireCkycResponse,
  updateRisk_address,
  updateInsured_details,
  updateNominee_details,
  updateBgrConsentId,
  updateBgrConsentStatus,
  updateBgrPlanQuoteRes,
  updateQuoteListArray,
  updateBgrCkycResponse,
  updateBgrCkycCustomerData,
  resetBgrStore,
  updateBgrCkycResponseOvd,
  updateBgrAllData,
  updateFireAllData,
  updateFirePaymentResponse,
  updateRiskAddress,
  updateQuoteNumber,
  updateRiskOccupancy,
  updateQuotationFilters,
  updateBgrInitialConsentStatus,
  updateSafetyDetails,
  updateFireSpConsentId,
  updateFireSpConsentStatus,
  updateFireSpConsentSentAt,
  updateFireSelectedSp,
} = fireSlice.actions

export {
  reducer as fireReducer,
  resetFire,
  updatePageOneAnswers,
  updatePageTwoAnswers,
  updatePageThreeAnswers,
  updateRiskPincode,
  updatePropertyDetails,
  updateBgrRiskPincode,
  updateFirequoteRes,
  updateSelectedQuote,
  updateBgrFilters,
  updateBgrCkycResponseOvd,
  updateBgrAddOn,
  resetBgrCkyc,
  resetFireCkycResponse,
  updateBgrAdditionalCoverages,
  updateBgrCkycCustomerData,
  updateSelectedPlan,
  updateIsBgrQuotationApiLoading,
  updateRisk_address,
  updateBgrCkycResponse,
  updateInsured_details,
  updateNominee_details,
  updateBgrConsentId,
  updateBgrConsentStatus,
  updateBgrPlanQuoteRes,
  updateQuoteListArray,
  resetBgrStore,
  updateBgrAllData,
  updateFireAllData,
  updateFirePaymentResponse,
  updateRiskOccupancy,
  updateQuotationFilters,
  updateRiskAddress,
  updateQuoteNumber,
  updateBgrInitialConsentStatus,
  updateSafetyDetails,
  updateFireSpConsentId,
  updateFireSpConsentStatus,
  updateFireSpConsentSentAt,
  updateFireSelectedSp,
}
